import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Save, Trash2, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Manager-only — admin-managed Technician Specialities.
 *
 * Mirrors ManageAssetCategoriesDialog but hits /technician-specialities.
 * Renaming cascades to db.technicians.specialities (array field), so no
 * technician-side references go stale.
 */
const ManageTechnicianSpecialitiesDialog = ({ open, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/technician-specialities");
      setRows(data || []);
    } catch {
      toast.error("Could not load specialities");
    }
  };
  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const duplicate = (() => {
    const n = newName.trim().toLowerCase();
    if (!n) return null;
    return rows.find((r) => r.name.toLowerCase() === n) || null;
  })();

  const create = async () => {
    const name = newName.trim();
    if (!name || duplicate) return;
    setBusy(true);
    try {
      await api.post("/technician-specialities", { name });
      setNewName("");
      await load();
      onChanged?.();
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const save = async (row) => {
    setBusy(true);
    try {
      await api.put(`/technician-specialities/${row.id}`, {
        name: row.name,
        sort: row.sort,
      });
      await load();
      onChanged?.();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete speciality "${row.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/technician-specialities/${row.id}`);
      await load();
      onChanged?.();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const move = (idx, dir) => {
    const nr = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= nr.length) return;
    [nr[idx], nr[j]] = [nr[j], nr[idx]];
    nr.forEach((r, i) => (r.sort = i));
    setRows(nr);
    api.put(`/technician-specialities/${nr[idx].id}`, { sort: idx }).catch(() => {});
    api
      .put(`/technician-specialities/${nr[j].id}`, { sort: j })
      .then(onChanged)
      .catch(() => {});
  };

  const update = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="manage-tech-specialities-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(640px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Technicians</div>
            <h3 className="font-display text-2xl">Manage specialities</h3>
          </div>
          <button
            data-testid="manage-tech-spec-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          {rows.length === 0 && (
            <div className="text-sm text-[#607087] italic text-center py-4">
              No specialities yet. Add "3D printer", "CNC", "Sanders" etc so
              staff pick from a list instead of retyping.
            </div>
          )}
          {rows.map((row, idx) => (
            <div
              key={row.id}
              data-testid={`tech-spec-row-${row.name}`}
              className="border-2 border-black rounded-xl p-3 flex items-center gap-2"
            >
              <div className="flex gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="nb-btn bg-white p-1.5 disabled:opacity-30"
                  aria-label="up"
                >
                  <ArrowUp size={12} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === rows.length - 1}
                  className="nb-btn bg-white p-1.5 disabled:opacity-30"
                  aria-label="down"
                >
                  <ArrowDown size={12} strokeWidth={2.5} />
                </button>
              </div>
              <input
                data-testid={`tech-spec-name-${row.name}`}
                className="nb-input flex-1"
                value={row.name}
                onChange={(e) => update(row.id, { name: e.target.value })}
              />
              <button
                data-testid={`tech-spec-save-${row.name}`}
                onClick={() => save(row)}
                disabled={busy}
                className="nb-btn nb-btn-primary px-3 py-2 text-sm flex items-center gap-1"
              >
                <Save size={14} strokeWidth={2.5} /> Save
              </button>
              <button
                data-testid={`tech-spec-delete-${row.name}`}
                onClick={() => remove(row)}
                disabled={busy}
                className="nb-btn nb-btn-danger p-2"
              >
                <Trash2 size={14} strokeWidth={2.5} />
              </button>
            </div>
          ))}

          <div className="border-2 border-dashed border-[#001740] rounded-xl p-3 flex items-end gap-3 bg-[#F4F8FB]">
            <label className="flex-1">
              <div className="label-eyebrow mb-1">New speciality</div>
              <input
                data-testid="new-tech-spec-name"
                className="nb-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !duplicate && create()}
                placeholder="e.g. 3D printer, CNC, Sanders…"
              />
            </label>
            <button
              data-testid="new-tech-spec-add"
              onClick={create}
              disabled={busy || !newName.trim() || !!duplicate}
              className="nb-btn nb-btn-success px-4 py-2 text-sm flex items-center gap-1"
            >
              <Plus size={14} strokeWidth={2.5} /> Add
            </button>
          </div>

          {duplicate && (
            <div
              data-testid="tech-spec-duplicate-warning"
              className="text-xs font-semibold px-3 py-2 rounded-md border-2 border-[#C7A43B] bg-[#FFF7DB] text-[#5B4500]"
            >
              {`"${duplicate.name}" already exists — edit its row above instead.`}
            </div>
          )}

          <div className="label-eyebrow text-[#607087] mt-1">
            Renaming cascades to every technician tagged with the old name.
            Delete is blocked while a technician still uses it.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTechnicianSpecialitiesDialog;
